import { Autocomplete, Box, Chip, createFilterOptions, Stack, TextField } from "@mui/material";
import { omit } from "lodash-es";
import type { FC } from "react";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import { useSupabaseCallback } from "../../server/supabase/hooks";
import { SupabaseTagsService } from "./server/supabase-tags-service";
import type { Tag } from "./types";

const filter = createFilterOptions<Tag | string>();

export const TagManager: FC<{
  localTags: Tag[];
  setLocalTags: (tags: Tag[]) => void;
  availableTags?: Tag[];
}> = ({ localTags, setLocalTags, availableTags = [] }) => {
  const [value, setValue] = useState<Tag | null>(null);
  const [inputText, setInputText] = useState<string>("");

  const handleDelete = useCallback(
    (tag: Tag) => {
      if (!localTags) return;

      const updatedTags = localTags.filter((t) => t.id !== tag.id);
      setLocalTags(updatedTags);
    },
    [setLocalTags, localTags],
  );

  const handleChange = useSupabaseCallback(
    async ({ supabase }, newValue: string | Tag | null): Promise<void> => {
      if (!newValue) {
        return;
      }

      if (typeof newValue === "string") {
        // If it is free typed we need to create a new tag
        const val = newValue.trim();

        if (localTags.find((x) => x.name === val)) {
          setValue(null);
          setInputText("");
          return;
        }

        const { data, error } = await new SupabaseTagsService(supabase).upsert({ name: val });

        if (error || !data) {
          toast.error(
            error?.message.includes("tag_name_unique_per_account")
              ? `Tag name "${val}" already exists`
              : "Failed to create tag",
          );
          setValue(null);
          setInputText("");
          return;
        }

        const newTags = [...localTags, data];
        setLocalTags(newTags);
      } else {
        const newTags = [...localTags, newValue];
        setLocalTags(newTags);
      }

      setValue(null);
      setInputText("");
    },
    [localTags, setLocalTags],
  );

  return (
    <Box mt={2}>
      <Autocomplete<Tag | string>
        value={value}
        inputValue={inputText}
        onChange={(_, newValue) => {
          void handleChange(newValue);
        }}
        onInputChange={(_, newInputValue) => setInputText(newInputValue)}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const inputValue = params.inputValue;
          const isExisting = options.some(
            (option) => typeof option !== "string" && option.name === inputValue,
          );
          if (inputValue !== "" && !isExisting) {
            filtered.push(inputValue);
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        id="tag-manager-input"
        options={availableTags.filter((option) => !localTags.some((t) => t.id === option.id))}
        getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
        renderOption={(props, option) => {
          const { ...optionProps } = omit(props, "key");
          const label = typeof option === "string" ? option : option.name;
          return (
            <li key={label} {...optionProps}>
              <Chip label={label} color="primary" />
            </li>
          );
        }}
        fullWidth
        renderInput={(params) => <TextField {...params} label="Add tags" />}
      />
      {localTags.length > 0 && (
        <Stack direction="row" mt={2} gap={1} useFlexGap flexWrap="wrap">
          {localTags.map((tag, index) => (
            <Chip
              key={index}
              label={tag.name}
              color="primary"
              onDelete={() => void handleDelete(tag)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
